import { Select } from 'antd';
import { useGroupFee } from 'app/hooks/adminUsers/groupFee';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ClassificationSelectProps {
  defaultValue?: string | string[] | number;
  setSelectLevel: (value: any) => void;
  disabled?: boolean;
  mode?: 'multiple' | undefined;
}

const ClassificationSelect = ({
  defaultValue,
  setSelectLevel,
  disabled = false,
  mode,
}: ClassificationSelectProps) => {
  // 초기값 처리
  const [value, setValue] = useState(() => {
    if (mode === 'multiple') {
      return Array.isArray(defaultValue) ? defaultValue : [];
    }
    return defaultValue ? String(defaultValue) : '';
  });

  const { t } = useTranslation();
  const { data: list } = useGroupFee();

  const options = useMemo(() => {
    if (!list) return [];

    const groupOptions = list.map((group: any) => ({
      value: String(group.level),
      label: group.name,
    }));

    return [{ value: '*', label: t('All') }, ...groupOptions];
  }, [list, mode, t]);

  /**
   * 복수선택 핸들러
   * 전체 선택 외에 다른 값이 있으면 전체 선택 제거
   * 아무런 값도 선택하지 않으면 전체 선택
   */
  const handleMultipleChange = useCallback(
    (selectedValues: string[]) => {
      let processedValues = selectedValues;
      const currentValue = Array.isArray(value) ? value : [];

      if (selectedValues.length === 0) {
        processedValues = ['*'];
      } else if (selectedValues.includes('*') && !currentValue.includes('*')) {
        processedValues = ['*'];
      } else if (selectedValues.includes('*') && selectedValues.length > 1) {
        processedValues = selectedValues.filter((item) => item !== '*');
      }

      setValue(processedValues);
      setSelectLevel(processedValues);
    },
    [value, setSelectLevel]
  );

  // 단일선택 핸들러
  const handleSingleChange = (selectedValue: string) => {
    setValue(selectedValue);
    // 숫자로 변환 가능한 경우 숫자로, 아니면 문자열 그대로 전달
    const numericValue = !isNaN(Number(selectedValue)) ? Number(selectedValue) : selectedValue;
    setSelectLevel(numericValue);
  };

  // 통합 핸들러
  const handleChange = (selectedValue: string | string[]) => {
    if (mode === 'multiple') {
      handleMultipleChange([...selectedValue] as string[]);
    } else {
      handleSingleChange(selectedValue as string);
    }
  };

  useEffect(() => {
    if (mode === 'multiple') {
      setValue(Array.isArray(defaultValue) ? defaultValue : []);
    } else {
      setValue(defaultValue ? String(defaultValue) : '');
    }
  }, [defaultValue, mode])

  return (
    <Select
      value={value}
      disabled={disabled}
      onChange={handleChange}
      options={options}
      mode={mode}
      defaultValue={defaultValue ? String(defaultValue) : undefined}
      style={{ width: '100%'}}
    />
  );
};

export default React.memo(ClassificationSelect);
